import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    UserPlus, Trash2, Edit2, MapPin, Phone, Mail,
    ShoppingBag, ClipboardCheck, CheckCircle, XCircle,
    Search, Filter, Eye, EyeOff, RefreshCw, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { useAuth } from '../App';
import { API } from '../App';

const AdminSalesmen = () => {
    const { token } = useAuth();
    const [salesmen, setSalesmen] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedSalesman, setSelectedSalesman] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [addForm, setAddForm] = useState({
        name: '', email: '', password: '', phone: '', zone: ''
    });
    const [editForm, setEditForm] = useState({
        name: '', phone: '', zone: '', status: 'active'
    });

    const headers = { Authorization: `Bearer ${token}` };

    const fetchSalesmen = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/admin/salesmen`, { headers });
            setSalesmen(res.data);
        } catch (e) {
            toast.error('Failed to load salesmen');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchSalesmen(); }, []);

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!addForm.name || !addForm.email || !addForm.password) {
            return toast.error('Name, email and password are required');
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/api/admin/salesmen`, addForm, { headers });
            toast.success(`Salesman ${addForm.name} created successfully`);
            setShowAddModal(false);
            setAddForm({ name: '', email: '', password: '', phone: '', zone: '' });
            fetchSalesmen();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to create salesman');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.put(`${API}/api/admin/salesmen/${selectedSalesman.id}`, editForm, { headers });
            toast.success('Salesman updated');
            setShowEditModal(false);
            fetchSalesmen();
        } catch (e) {
            toast.error('Failed to update salesman');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setSubmitting(true);
        try {
            await axios.delete(`${API}/api/admin/salesmen/${selectedSalesman.id}`, { headers });
            toast.success('Salesman removed');
            setShowDeleteModal(false);
            fetchSalesmen();
        } catch (e) {
            toast.error('Failed to remove salesman');
        } finally {
            setSubmitting(false);
        }
    };

    const openEdit = (s) => {
        setSelectedSalesman(s);
        setEditForm({ name: s.name, phone: s.phone || '', zone: s.zone || '', status: s.status || 'active' });
        setShowEditModal(true);
    };

    const openDelete = (s) => { setSelectedSalesman(s); setShowDeleteModal(true); };

    const filtered = salesmen.filter(s =>
        s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.zone?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const totalOrders = salesmen.reduce((sum, s) => sum + (s.orders_placed || 0), 0);
    const totalVisits = salesmen.reduce((sum, s) => sum + (s.visits_completed || 0), 0);

    return (
        <DashboardLayout userRole="admin">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Users className="w-7 h-7 text-primary" />
                            Field Salesmen Management
                        </h1>
                        <p className="text-muted-foreground text-sm mt-1">
                            Add, remove and track field agents who visit customers and place orders
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="icon" onClick={fetchSalesmen}>
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button onClick={() => setShowAddModal(true)} className="gap-2 btn-primary">
                            <UserPlus className="w-4 h-4" />
                            Add Salesman
                        </Button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                        { label: 'Total Salesmen', value: salesmen.length, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20' },
                        { label: 'Active', value: salesmen.filter(s => s.status === 'active').length, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20' },
                        { label: 'Visits Completed', value: totalVisits, icon: ClipboardCheck, color: 'text-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/20' },
                        { label: 'Orders Placed', value: totalOrders, icon: ShoppingBag, color: 'text-orange-600', bg: 'bg-orange-50 dark:bg-orange-900/20' },
                    ].map((stat, i) => (
                        <div key={i} className="bg-card border rounded-xl p-4">
                            <div className={`w-10 h-10 ${stat.bg} rounded-lg flex items-center justify-center mb-3`}>
                                <stat.icon className={`w-5 h-5 ${stat.color}`} />
                            </div>
                            <p className="text-2xl font-bold">{stat.value}</p>
                            <p className="text-xs text-muted-foreground">{stat.label}</p>
                        </div>
                    ))}
                </div>

                {/* Search */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Search by name, email, or zone..."
                        className="pl-9"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>

                {/* Table */}
                <div className="bg-card border rounded-xl overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-16 text-muted-foreground">
                            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p className="font-medium">No salesmen found</p>
                            <p className="text-sm">Click "Add Salesman" to create your first field agent</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b bg-muted/50 text-sm text-muted-foreground">
                                        <th className="text-left px-4 py-3 font-medium">Salesman</th>
                                        <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Contact</th>
                                        <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Zone</th>
                                        <th className="text-center px-4 py-3 font-medium">Assigned</th>
                                        <th className="text-center px-4 py-3 font-medium">Completed</th>
                                        <th className="text-center px-4 py-3 font-medium">Orders</th>
                                        <th className="text-center px-4 py-3 font-medium">Status</th>
                                        <th className="text-right px-4 py-3 font-medium">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((s, i) => (
                                        <tr key={s.id} className={`border-b last:border-0 hover:bg-muted/30 transition-colors ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">
                                                        {s.name?.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-sm">{s.name}</p>
                                                        <p className="text-xs text-muted-foreground">{s.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 hidden md:table-cell">
                                                <div className="space-y-0.5">
                                                    {s.phone && <p className="text-sm flex items-center gap-1"><Phone className="w-3 h-3" />{s.phone}</p>}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 hidden md:table-cell">
                                                {s.zone ? (
                                                    <span className="flex items-center gap-1 text-sm"><MapPin className="w-3 h-3 text-primary" />{s.zone}</span>
                                                ) : <span className="text-muted-foreground text-sm">—</span>}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="font-semibold text-blue-600">{s.visits_assigned || 0}</span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="font-semibold text-green-600">{s.visits_completed || 0}</span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="font-semibold text-orange-600">{s.orders_placed || 0}</span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <Badge variant={s.status === 'active' ? 'default' : 'secondary'}
                                                    className={s.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30' : ''}>
                                                    {s.status || 'active'}
                                                </Badge>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Button size="icon" variant="ghost" onClick={() => openEdit(s)} title="Edit">
                                                        <Edit2 className="w-4 h-4" />
                                                    </Button>
                                                    <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => openDelete(s)} title="Remove">
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Add Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md border">
                        <div className="p-6 border-b">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                <UserPlus className="w-5 h-5 text-primary" />
                                Add New Field Salesman
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">They will use these credentials to log in to SolarSavers</p>
                        </div>
                        <form onSubmit={handleAdd} className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2 space-y-1.5">
                                    <Label htmlFor="sm-name">Full Name *</Label>
                                    <Input id="sm-name" placeholder="e.g. Rahul Sharma" value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} required />
                                </div>
                                <div className="col-span-2 space-y-1.5">
                                    <Label htmlFor="sm-email">Email Address *</Label>
                                    <Input id="sm-email" type="email" placeholder="rahul@solarsavers.in" value={addForm.email} onChange={e => setAddForm({ ...addForm, email: e.target.value })} required />
                                </div>
                                <div className="col-span-2 space-y-1.5">
                                    <Label htmlFor="sm-pass">Password *</Label>
                                    <div className="relative">
                                        <Input id="sm-pass" type={showPassword ? 'text' : 'password'} placeholder="Min 8 characters" value={addForm.password} onChange={e => setAddForm({ ...addForm, password: e.target.value })} required className="pr-10" />
                                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="sm-phone">Phone</Label>
                                    <Input id="sm-phone" placeholder="9876543210" value={addForm.phone} onChange={e => setAddForm({ ...addForm, phone: e.target.value })} />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="sm-zone">Coverage Zone</Label>
                                    <Input id="sm-zone" placeholder="e.g. Delhi NCR" value={addForm.zone} onChange={e => setAddForm({ ...addForm, zone: e.target.value })} />
                                </div>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowAddModal(false)}>Cancel</Button>
                                <Button type="submit" className="flex-1 btn-primary" disabled={submitting}>
                                    {submitting ? 'Creating...' : 'Create Salesman'}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedSalesman && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md border">
                        <div className="p-6 border-b">
                            <h2 className="text-xl font-bold">Edit Salesman</h2>
                            <p className="text-sm text-muted-foreground">{selectedSalesman.email}</p>
                        </div>
                        <form onSubmit={handleEdit} className="p-6 space-y-4">
                            <div className="space-y-1.5">
                                <Label>Full Name</Label>
                                <Input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Phone</Label>
                                <Input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Coverage Zone</Label>
                                <Input value={editForm.zone} onChange={e => setEditForm({ ...editForm, zone: e.target.value })} />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Status</Label>
                                <select className="w-full border rounded-lg px-3 py-2 bg-background text-sm" value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowEditModal(false)}>Cancel</Button>
                                <Button type="submit" className="flex-1 btn-primary" disabled={submitting}>
                                    {submitting ? 'Saving...' : 'Save Changes'}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirm Modal */}
            {showDeleteModal && selectedSalesman && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm border p-6 text-center">
                        <div className="w-14 h-14 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Trash2 className="w-7 h-7 text-red-600" />
                        </div>
                        <h3 className="text-lg font-bold mb-2">Remove Salesman?</h3>
                        <p className="text-muted-foreground text-sm mb-6">
                            Are you sure you want to remove <strong>{selectedSalesman.name}</strong>? This action cannot be undone.
                            Their historical orders and visits will be retained.
                        </p>
                        <div className="flex gap-3">
                            <Button variant="outline" className="flex-1" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                            <Button className="flex-1 bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={handleDelete} disabled={submitting}>
                                {submitting ? 'Removing...' : 'Remove'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

export default AdminSalesmen;
